import React, { useMemo, useState } from "react";
import { graphql } from "gatsby";
import Layout from "../components/layout";
import Seo from "../components/seo";
import PageContentBody from "../components/page-content-body";
import withPreview from "../utility/with-preview";
import SiteOrgSchema from "../components/site-org-schema";
import placeholderLogoImg from "../images/logo_haier_search.jpg";
import { getCampaignBoxes } from "../utility/campaigns";

const getGeneratedLocale = (liferayFields) => {
  const localeCategory = liferayFields?.taxonomyCategoryBriefs?.find(
    (category) => category?.parentTaxonomyVocabulary?.name === "locale-pages"
  );
  return localeCategory ? `/locale-${localeCategory.liferayId}/` : null;
};

const IndexPage = ({
  data: {
    liferayPage,
    config,
    labelManagerLabels,
    newMainMenuFiltered,
    globalCampaignsBannerTop,
    liferayMenuConfiguration,
  },
  location,
}) => {
  const campaignBoxesBannerTop = useMemo(
    () => getCampaignBoxes(globalCampaignsBannerTop),
    [globalCampaignsBannerTop]
  );
  const resultBannerTop =
    campaignBoxesBannerTop?.filter((item) => !!item.liferayFields.articleId) || [];
  const [isAnchor, setIsAnchor] = useState();

  const generatedLocale = useMemo(
    () => getGeneratedLocale(liferayPage.liferayFields),
    [liferayPage.liferayFields]
  );
  const isNewMenu =
    !!newMainMenuFiltered &&
    newMainMenuFiltered?.name &&
    !!liferayMenuConfiguration?.newMenuActive &&
    liferayMenuConfiguration?.copyrightText !== "";

  return (
    <Layout
      bannerTop={
        liferayPage?.customModule?.find((module) => module.__typename === "LiferayBannerTopMenu")
          ?.liferayFields?.articleId
          ? liferayPage?.customModule?.find(
              (module) => module.__typename === "LiferayBannerTopMenu"
            )
          : resultBannerTop[0]
      }
      location={location}
      isEppMenu={liferayPage.eppMenu?.[0] === "yes"}
      isAnchor={isAnchor}
      hasSimpleMenu={liferayPage.hasSimpleMenu?.[0] === "yes"}
      customFooter={liferayPage?.customModule?.find(
        (module) => module.__typename === "LiferayFooterMenu"
      )}
      hideMainBenefits={liferayPage.hideMainBenefits?.[0]}
      mainBenefits={
        liferayPage.hideMainBenefits?.[0] !== "yes"
          ? liferayPage?.customModule?.filter(
              (module) => module.__typename === "LiferayMainBenefits"
            )
          : null
      }
      generatedLocale={generatedLocale}
      newMenu={!!isNewMenu}
    >
      <SiteOrgSchema description={liferayPage.seo?.descriptionSeo} />
      <PageContentBody
        page={liferayPage}
        location={location}
        origin="Homepage"
        config={config}
        labelManagerLabels={labelManagerLabels}
        showTabs={false}
        showBreadcrumb={false}
        setIsAnchor={setIsAnchor}
      />
    </Layout>
  );
};

export const Head = ({ data: { liferayPage, labelManagerLabels } }) => {
  const ogImage = useMemo(() => {
    const sliderHero = liferayPage.contentBody?.length ? liferayPage.contentBody[0] : null;
    if (sliderHero?.moduleTitleFieldSet) {
      const module = sliderHero.moduleTitleFieldSet.find((module) =>
        module?.titleT1FieldSet?.some(
          (item) => item.bgImageMobileT1FieldSet.bgImageMobileT1 || item.bgImageT1FieldSet.bgImageT1
        )
      );
      const moduleSlider = sliderHero.moduleTitleFieldSet.find((module) =>
        module?.titleT1FieldSet?.some(
          (item) => item.slideForegroundImageFieldSet.slideForegroundImage
        )
      );
      return module
        ? module.titleT1FieldSet.find((item) => item.bgImageT1FieldSet.bgImageMobileT1)
          ? module.titleT1FieldSet.find((item) => item.bgImageT1FieldSet).bgImageT1FieldSet
              .bgImageMobileT1.node.resize
          : moduleSlider
          ? moduleSlider.titleT1FieldSet.find(
              (item) => item.slideForegroundImageFieldSet.slideForegroundImage
            ).slideForegroundImageFieldSet.slideForegroundImage.node.resize
          : module.titleT1FieldSet.find((item) => item.bgImageT1FieldSet.bgImageT1)
              .bgImageT1FieldSet.bgImageT1.node.resize
        : placeholderLogoImg;
    }
    return placeholderLogoImg;
  }, [liferayPage.contentBody]);

  const generatedLocale = useMemo(
    () => getGeneratedLocale(liferayPage.liferayFields),
    [liferayPage.liferayFields]
  );

  return (
    <Seo
      title={labelManagerLabels.home}
      path="/"
      ogImage={ogImage}
      seo={liferayPage.seo}
      hrefLanguages={liferayPage.fields?.hrefLanguages}
      generatedLocale={generatedLocale}
    />
  );
};

export default withPreview(IndexPage, { subField: "liferayPage", fixed: true });
export const query = graphql`
  {
    liferayPage(slug: { eq: "/" }) {
      liferayFields {
        siteId
        title
        articleId
        taxonomyCategoryBriefs {
          liferayId
          name
          parentTaxonomyVocabulary {
            name
          }
        }
      }
      eppMenu
      hasSimpleMenu
      hideMainBenefits
      slug
      customModule {
        __typename
        ... on LiferayBannerTopMenu {
          ...BannerTopMenuFragment
        }
        ... on LiferayMainBenefits {
          id
          fieldsGroup {
            mainBenefitsTitle
            mainBenefitsIcon {
              value {
                description
              }
              node {
                publicURL
              }
            }
            mainBenefitsPosition
            mainBenefitsLink
          }
        }
        ... on LiferayFooterMenu {
          columnNameFieldSet {
            columnName
            page {
              ... on LiferayPage {
                liferayFields {
                  title
                }
                slug
              }
              ... on LiferayPageLine {
                liferayFields {
                  title
                }
                slug
              }
              ... on LiferayPageCrossLine {
                liferayFields {
                  title
                }
                slug
              }
              ... on LiferayPageExternalLink {
                liferayFields {
                  title
                }
                externalLink
                target
              }
            }
          }
          payAndSocialFooter {
            columnPayAndSocial
            pagePayAndSocial {
              ... on LiferayPageExternalLink {
                liferayFields {
                  title
                }
                icon
                externalLink
                target
                alternativeIcon {
                  alternativeIcon {
                    value {
                      description
                    }
                    node {
                      publicURL
                    }
                  }
                  height
                  width
                }
              }
            }
          }
          legalInfo
          localeContent {
            ... on LiferayPageExternalLink {
              liferayFields {
                title
              }
              icon
              image {
                node {
                  resize(width: 20, quality: 100) {
                    src
                  }
                }
                value {
                  description
                }
              }
              externalLink
              target
            }
          }
        }
      }
      seo {
        ...SeoFragment
      }
      fields {
        hrefLanguages
      }
      ...ContentBodyFragment
    }
    config: liferayGeneralConfiguration {
      haierStEcommLines {
        productLine
      }
      isReevooActiveFieldSet {
        isReevooActive
        isBazaarVoiceActive
      }
      virtualRealityActiveFieldSet {
        virtualRealityActive
      }
      wtbAndRetailersActive
      priceInPerc: priceInPerc
      tagActive
      tagColor
      invertProductFamily
    }
    labelManagerLabels: labelManagerLabels {
      home
      addToCart
      whereToBuy
      showProduct
      compare
      buyOnHaierShop
      buyNow
    }
    newMainMenuFiltered {
      name
    }
    liferayMenuConfiguration {
      newMenuActive
      copyrightText
    }
    globalCampaignsBannerTop: globalCampaigns(positions: ["BANNER TOP | Banner Promo"]) {
      date {
        pmStart
        pmEnd
      }
      filterField {
        pmInStock
        pmSpecialPrice
      }
      boxAndPosition {
        pmBoxPosition
        webContent {
          ...BannerTopMenuFragment
        }
      }
    }
  }
`;
